import { useEffect, useState } from 'react';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { AlertBox, Dropdown, DropdownOption, Typography } from '@vp/swan';
import {
    DESIGN_REQUEST_OPTION_RECREATE,
    DESIGN_REQUEST_OPTION_TOUCH_UP,
} from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { useFieldViewSkinContext } from '../../../../context/FieldViewContext';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { FormInput } from '../../BriefForm';
import { SubText } from '../../SubText';
import { Field_SelectChoiceField_Fragment } from '../../brief.generated';
import { briefTestId } from '../../testIds';
import { useUpdateSelectChoiceField } from './useUpdateSelectChoiceField';

export type SelectFieldFieldProps = Field_SelectChoiceField_Fragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
};

export function Input({
    id,
    register,
    required,
    placeholder,
    options,
    choiceValue,
    subtext,
    setValue,
    errorMessage,
}: SelectFieldFieldProps) {
    const DEFAULT_VALUE = placeholder ? '' : options[0].value;
    const [selectedValue, setSelectedValue] = useState<string>(choiceValue || DEFAULT_VALUE);
    const { briefId } = useBriefFormContext();
    // TODO: This hardcoded ID is for experimental code only. Remove or make configurable after experiment.
    // Experiment: EPIT-1402
    const isDesignType = id === 'designServiceType';
    const updateMutation = useUpdateSelectChoiceField(id, briefId, choiceValue, isDesignType);
    const errorMsg = errorMessage || __('This field is required');

    useEffect(() => {
        setValue(id, selectedValue);
        if (selectedValue) {
            updateMutation(selectedValue);
        }
    }, [id, setValue, selectedValue, updateMutation]);

    return (
        <>
            <Dropdown
                data-testid={briefTestId('brief-field-select', id)}
                defaultValue={selectedValue}
                {...register(id, {
                    required: required ? errorMsg : false,
                    onChange: (e) => setSelectedValue(e.target.value),
                })}
                fullWidth
                aria-required={required ? true : undefined}
            >
                <DropdownOption
                    key={`${id}-empty`}
                    value=""
                    hidden
                    disabled
                    data-testid={briefTestId('brief-field-select-option', id, 'empty')}
                >
                    {placeholder || ''}
                </DropdownOption>
                {options.map((i) => {
                    return (
                        <DropdownOption
                            key={i.value}
                            value={i.value}
                            data-testid={briefTestId('brief-field-select-option', id, i.value)}
                        >
                            {i.label}
                        </DropdownOption>
                    );
                })}
            </Dropdown>
            {subtext && (
                <SubText data-testid={briefTestId('brief-field-subtext', id)}>{subtext}</SubText>
            )}
            {isDesignType && selectedValue === DESIGN_REQUEST_OPTION_TOUCH_UP && (
                <AlertBox data-testid={briefTestId('brief-field-alert', id)} mt={6}>
                    {__(
                        'You’ll need to provide a source file for an existing design, we’ll ask you to upload a file or select an existing VistaPrint project in a subsequent step.'
                    )}
                </AlertBox>
            )}
            {isDesignType && selectedValue === DESIGN_REQUEST_OPTION_RECREATE && (
                <AlertBox data-testid={briefTestId('brief-field-alert', id)} mt={6}>
                    {__(
                        'You’ll need to provide a photo or a screenshot of a flat design. Not suitable for recreating complex AI generated designs, images or illustrations.'
                    )}
                </AlertBox>
            )}
        </>
    );
}

interface SelectFieldViewProps {
    options: Field_SelectChoiceField_Fragment['options'];
    choiceValue: Field_SelectChoiceField_Fragment['choiceValue'];
}

export function View({ options, choiceValue }: SelectFieldViewProps) {
    const skin = useFieldViewSkinContext();

    const option = options.find((o) => o.value === choiceValue);
    if (!option) return null;

    return <Typography fontSize={skin.fontSize}>{option.label}</Typography>;
}
